import React, { useMemo, useState } from 'react';
import { Activity, Download, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useWebSocket } from '../hooks/useWebSocket';
import { getWebSocketHttpBase } from '../lib/apiBase';

export function LogsPage() {
  const wsUrl = `${getWebSocketHttpBase()}/ws`;
  const { isConnected, lastMessage } = useWebSocket(wsUrl);
  const [events, setEvents] = useState([]);

  React.useEffect(() => {
    if (!lastMessage) return;
    setEvents((prev) => [
      {
        ts: new Date().toISOString(),
        type: lastMessage.type || 'message',
        payload: lastMessage,
      },
      ...prev,
    ].slice(0, 200));
  }, [lastMessage]);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(events, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aerta-events-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const systemUpdates = useMemo(() => events.filter((e) => e.type === 'system_update'), [events]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Events</h1>
          <p className="text-muted-foreground">Live websocket stream for pilot runtime evidence.</p>
        </div>
        <div className="flex gap-2">
          <Badge variant={isConnected ? 'success' : 'destructive'}>{isConnected ? 'Connected' : 'Disconnected'}</Badge>
          <Button variant="outline" onClick={exportJson}><Download className="mr-2 h-4 w-4" />Export</Button>
          <Button variant="outline" onClick={() => setEvents([])}><RefreshCw className="mr-2 h-4 w-4" />Clear</Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>System Update Count</CardTitle><CardDescription>Messages with `type=system_update`.</CardDescription></CardHeader>
        <CardContent><p className="text-2xl font-bold">{systemUpdates.length}</p></CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Activity className="h-4 w-4" />Recent Events</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {events.length === 0 ? <p className="text-sm text-muted-foreground">No events received yet.</p> : null}
            {events.map((event, idx) => (
              <div key={`${event.ts}-${idx}`} className="rounded-md border border-border p-3">
                <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{event.type}</span><span>{new Date(event.ts).toLocaleTimeString()}</span>
                </div>
                <pre className="overflow-x-auto text-xs">{JSON.stringify(event.payload, null, 2)}</pre>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
